












































































































































































































































































































































































































































































































import Vue, { PropType } from 'vue';
import * as InputValidation from '@/mixins/inputValidation';

import store from '@/store';

import { WebRequestStatus } from '@/models';
import { MultiPointPacket, Badge, Objective, Outcome } from '@/models/packet';
import { IconData } from '@/icons';
import icons from '@/icons';

import Goal from '@/components/Goal.vue';
import HideableGoal from '@/components/HideableGoal.vue';
import Icon from '@/components/Icon.vue';
import MultiPointBasicInfoEditor from '@/components/MultiPointBasicInfoEditor.vue';
import MultiPointObjectiveInput from '@/components/MultiPointObjectiveInput.vue';
import MultiPointObjectiveEditor from '@/components/MultiPointObjectiveEditor.vue';
import MultiPointOutcomeEditor from '@/components/MultiPointOutcomeEditor.vue';
import MultiPointOutcomeInput from '@/components/MultiPointOutcomeInput.vue';
import MultiPointOutcomeEditorButton from '@/components/MultiPointOutcomeEditorButton.vue';
import PersistableItem from '@/components/PersistableItem.vue';

const emptyEditableObjective: Objective = {
  objectiveId: -1,
  name: '',
  isActive: true,
  iconData: icons.lookup('MaterialDesignIconFont', 0xf52d),
  description: '',
  useOutcomeIcon: false
};

const emptyEditableBadge: Badge = {
  badgeId: -1,
  description: '',
  objectiveId: -1,
  outcomeId: -1
};

const emptyEditableIndicator: Outcome = {
  outcomeId: -1,
  value: '',
  isActive: true,
  color: '',
  iconData: icons.lookup('MaterialDesignIconFont', 0xf52d),
  sortOrder: -1
};

enum Mode {
  EDIT,
  VIEW
}

const colorGenerator = (() => {
  const colors = ['#BEEF99', '#99BEEF', '#BE99EF'];

  let currentIndex = 0;

  return {
    next: () => {
      currentIndex = (currentIndex + 1) % colors.length;
      return colors[currentIndex];
    }
  };
})();

export default Vue.extend({
  components: {
    Goal,
    HideableGoal,
    Icon,
    MultiPointBasicInfoEditor,
    MultiPointObjectiveEditor,
    MultiPointObjectiveInput,
    MultiPointOutcomeEditor,
    MultiPointOutcomeInput,
    MultiPointOutcomeEditorButton,
    PersistableItem
  },

  props: {
    packet: Object as PropType<MultiPointPacket>,

    outcomes: {
      type: Array as PropType<[Outcome, WebRequestStatus][]>,
      required: true
    },

    objectives: {
      type: Array as PropType<[Objective, WebRequestStatus][]>,
      required: true
    },

    badges: {
      type: Array as PropType<[Badge, WebRequestStatus][]>,
      required: true
    },

    createIndicatorStatus: {
      type: Number as PropType<WebRequestStatus>,
      default: WebRequestStatus.READY
    },

    createObjectiveStatus: {
      type: Number as PropType<WebRequestStatus>,
      default: WebRequestStatus.READY
    },

    basicInfoStatus: {
      type: Number as PropType<WebRequestStatus>,
      default: WebRequestStatus.READY
    },

    alwaysEditing: {
      type: Boolean,
      default: false
    },

    disableEditing: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isEditingObjective: false,
      isEditingBadge: false,
      isEditingIndicator: false,
      isCreatingObjective: false,
      isCreatingIndicator: false,
      editableObjective: emptyEditableObjective,
      editableBadge: emptyEditableBadge,
      editableIndicator: emptyEditableIndicator,
      defaultObjective: {
        name: 'New Cog',
        description: 'Please, describe me.',
        iconData: icons.lookup('MaterialDesignIconFont', 0xf52d)
      },
      defaultIndicator: {
        value: 'New Column',
        color: colorGenerator.next()
      },
      createIndicatorIndex: 0,
      mode: this.alwaysEditing ? Mode.EDIT : Mode.VIEW
    };
  },

  computed: {
    rubricRows(): [
      [Objective, WebRequestStatus],
      [Badge, WebRequestStatus, boolean][]
    ][] {
      const outcomeLookup: {
        [key: number]: Outcome;
      } = this.outcomes.reduce(
        (acc, [outcome]) => ({
          ...acc,
          [outcome.outcomeId]: outcome
        }),
        {}
      );

      const outcomeIds = this.outcomes.map(([o]) => o.outcomeId);

      // Sort badges in the order of the outcomes they describe.
      return this.objectives.map(([objective, objectiveStatus]) => {
        return [
          [objective, objectiveStatus],
          this.badges
            .filter(
              ([badge]) =>
                badge.objectiveId === objective.objectiveId &&
                outcomeIds.includes(badge.outcomeId)
            )
            .sort(([badgeA], [badgeB]) => {
              return (
                outcomeLookup[badgeA.outcomeId].sortOrder -
                outcomeLookup[badgeB.outcomeId].sortOrder
              );
            })
            .map(([badge, status]) => [
              badge,
              status,
              objective.isActive && outcomeLookup[badge.outcomeId].isActive
            ])
        ] as [
          [Objective, WebRequestStatus],
          [Badge, WebRequestStatus, boolean][]
        ];
      });
    },

    colWidth(): number | undefined {
      if (this.$vuetify.breakpoint.xsOnly) {
        return 12;
      }

      if (this.$vuetify.breakpoint.smAndDown) {
        return 6;
      }

      return undefined;
    },

    isEditableBadgeDirty(): boolean {
      const { badgeId, description } = this.editableBadge;

      if (badgeId === -1) {
        return false;
      }

      const original = this.badges
        .map(([b]) => b)
        .find(b => b.badgeId === badgeId);

      if (!original) {
        return false;
      }

      return description !== original.description;
    },

    saveBadgeDisabled(): boolean {
      return (
        !this.isEditableBadgeDirty || !this.badgeDescriptionValidation.valid
      );
    },

    showCreateObjectiveStatus(): boolean {
      return this.createObjectiveStatus !== WebRequestStatus.READY;
    },

    showCreateIndicatorStatus(): boolean {
      return this.createIndicatorStatus !== WebRequestStatus.READY;
    },

    showEditOptions(): boolean {
      return this.mode === Mode.EDIT;
    },

    canEdit(): boolean {
      return this.packet.canEdit;
    },

    noIndicators(): boolean {
      return this.outcomes.length === 0;
    },

    noGoals(): boolean {
      return this.objectives.length === 0;
    },

    hasIndicators(): boolean {
      return !this.noIndicators;
    },

    hasGoals(): boolean {
      return !this.noGoals;
    },

    isInitialSetup(): boolean {
      return this.noGoals || this.noIndicators;
    },

    outcomeLookup(): { [key: number]: Outcome } {
      return this.outcomes.reduce((acc, [outcome]) => {
        return {
          ...acc,
          [outcome.outcomeId]: outcome
        };
      }, {});
    },

    badgeDescriptionValidation(): InputValidation.InputLengthValidation {
      return InputValidation.validateInput(
        this.editableBadge.description,
        1250,
        true
      );
    }
  },

  methods: {
    editObjectiveClose() {
      this.isEditingObjective = false;
    },

    editObjectiveStart(objective: Objective) {
      if (objective.objectiveId !== this.editableObjective.objectiveId) {
        this.editableObjective = { ...objective };
      }

      if (this.packet.packetId > 0) {
        store.dispatch.usage.getObjectiveUsage({
          packetId: this.packet.packetId,
          objectiveId: objective.objectiveId
        });
      } else {
        store.dispatch.usage.zero();
      }

      this.isEditingObjective = true;
    },

    saveObjective(details: {
      name: string;
      description: string;
      iconData: IconData;
    }) {
      this.$emit('update-objective', {
        ...this.editableObjective,
        ...details
      });

      this.isEditingObjective = false;
      this.editableObjective = emptyEditableObjective;
    },

    // Called from the edit objective dialog
    deactivateObjective() {
      this.$emit('update-objective', {
        ...this.editableObjective,
        isActive: false
      });

      this.isEditingObjective = false;
      this.editableObjective = emptyEditableObjective;
    },

    // Called from the packet edit screen
    activateObjective(objective: Objective) {
      this.$emit('update-objective', {
        ...objective,
        isActive: true
      });
    },

    deleteObjective(objective: Objective) {
      this.$emit('delete-objective', objective);

      this.editObjectiveClose();
      this.editableObjective = emptyEditableObjective;
    },

    editBadgeStart(badge: Badge) {
      if (badge.badgeId !== this.editableBadge.badgeId) {
        this.editableBadge = { ...badge };
      }

      this.isEditingBadge = true;
    },

    saveBadge(badge: Badge) {
      this.$emit('update-badge', badge);

      this.isEditingBadge = false;
    },

    editBadgeClose() {
      this.isEditingBadge = false;
    },

    editIndicatorStart(outcome: Outcome) {
      if (outcome.outcomeId !== this.editableIndicator.outcomeId) {
        this.editableIndicator = { ...outcome };
      }

      this.isEditingIndicator = true;

      if (this.packet.packetId > 0) {
        store.dispatch.usage.getOutcomeUsage({
          packetId: this.packet.packetId,
          outcomeId: outcome.outcomeId
        });
      } else {
        store.dispatch.usage.zero();
      }
    },

    saveIndicator(indicator: { value: string; color: string }) {
      this.$emit('update-indicator', {
        ...this.editableIndicator,
        ...indicator
      });

      this.isEditingIndicator = false;
      this.editableIndicator = emptyEditableIndicator;
    },

    // Called from the edit indicator dialog
    deactivateIndicator() {
      this.$emit('update-indicator', {
        ...this.editableIndicator,
        isActive: false
      });

      this.isEditingIndicator = false;
      this.editableIndicator = emptyEditableIndicator;
    },

    // Called from the packet edit screen (replaces the edit button)
    activateIndicator(outcome: Outcome) {
      this.$emit('update-indicator', {
        ...outcome,
        isActive: true
      });
    },

    deleteIndicator(outcome: Outcome) {
      this.$emit('delete-indicator', outcome);

      this.editIndicatorClose();
      this.editableIndicator = emptyEditableIndicator;
    },

    editIndicatorClose() {
      this.isEditingIndicator = false;
    },

    createObjectiveStart() {
      this.isCreatingObjective = true;
    },

    createObjectiveClose() {
      this.isCreatingObjective = false;
    },

    createObjective(details: {
      name: string;
      description: string;
      iconData: IconData;
    }) {
      this.$emit('create-objective', details);

      this.isCreatingObjective = false;
    },

    createIndicatorStart(index: number) {
      this.isCreatingIndicator = true;
      this.createIndicatorIndex = index;
    },

    createIndicatorClose() {
      this.isCreatingIndicator = false;
    },

    createIndicator(indicator: { value: string; color: string }) {
      this.$emit('create-indicator', {
        ...indicator,
        index: this.createIndicatorIndex
      });

      this.isCreatingIndicator = false;
      this.defaultIndicator.color = colorGenerator.next();
    },

    updatePacketInfo(info: { name: string; description: string }) {
      this.$emit('update-basic-info', info);
    },

    turnOnEditing() {
      this.mode = Mode.EDIT;
    },

    turnOffEditing() {
      this.mode = Mode.VIEW;
    }
  }
});
